import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Users2, Check, X, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import UpgradeDialog from "@/components/UpgradeDialog";

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

export default function CreateGroup() {
  const { user, logout } = useAuth();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: "" });
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();

  // Auto-derive slug from name until user edits it
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  // Debounced slug availability check
  useEffect(() => {
    if (!slug || slug.length < 3) {
      setSlugStatus({ checking: false, available: null, reason: "" });
      return;
    }
    setSlugStatus((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/groups/check-slug/${encodeURIComponent(slug)}`);
        setSlugStatus({ checking: false, available: data.available, reason: data.reason });
      } catch (e) {
        setSlugStatus({ checking: false, available: null, reason: "Could not verify" });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (slugStatus.available !== true) return toast.error("Please choose a valid, available slug");
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { name, slug, description });
      toast.success("Group created — you're the admin");
      localStorage.setItem("activeGroupSlug", data.slug);
      navigate(`/${data.slug}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        setUpgradeOpen(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
            <Users2 className="w-3.5 h-3.5" />
            Hi {user?.name?.split(" ")[0]}
          </div>
          <h1 className="heading text-4xl font-bold text-slate-900 tracking-tight mt-3">Create your group</h1>
          <p className="text-slate-500 mt-2">
            Pick a name and a unique link. You'll be the admin — the only one who can add members and manage categories.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl p-8 border border-slate-200/60 card-soft space-y-5"
          data-testid="create-group-form"
        >
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Group name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekend Friends"
              className="mt-2 h-12 rounded-2xl"
              required
              data-testid="create-group-name-input"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Group link</Label>
            <div className="mt-2 flex items-center rounded-2xl border border-slate-200 focus-within:border-slate-400 bg-white overflow-hidden">
              <span className="px-3 text-sm text-slate-400 select-none border-r border-slate-200 h-12 flex items-center">
                {window.location.host}/
              </span>
              <input
                value={slug}
                onChange={(e) => { setSlug(e.target.value.toLowerCase()); setSlugTouched(true); }}
                placeholder="weekend-friends"
                className="flex-1 h-12 px-3 outline-none bg-transparent text-slate-900 font-mono text-sm"
                required
                data-testid="create-group-slug-input"
              />
              <div className="pr-3 flex items-center h-12">
                {slugStatus.checking && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                {!slugStatus.checking && slugStatus.available === true && (
                  <Check className="w-4 h-4 text-emerald-600" data-testid="slug-check-ok" />
                )}
                {!slugStatus.checking && slugStatus.available === false && (
                  <X className="w-4 h-4 text-rose-600" data-testid="slug-check-fail" />
                )}
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-1.5">
              {slugStatus.checking && "Checking…"}
              {!slugStatus.checking && slugStatus.available === true && "This link is available."}
              {!slugStatus.checking && slugStatus.available === false && `Not available: ${slugStatus.reason}`}
              {!slugStatus.checking && slugStatus.available === null && "3-40 chars, lowercase letters, numbers, hyphens."}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this group for?"
              className="mt-2 rounded-2xl"
              data-testid="create-group-description-input"
            />
          </div>

          <Button
            type="submit"
            disabled={busy || slugStatus.available !== true}
            className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            data-testid="create-group-submit-button"
          >
            {busy ? "Creating…" : "Create group"}
          </Button>

          <div className="text-center text-xs text-slate-400">
            <button type="button" onClick={logout} className="hover:text-slate-700 underline">
              Sign out
            </button>
          </div>
        </form>
      </motion.div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} onUpgraded={() => window.location.reload()} />
    </div>
  );
}
